import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Package, Plus, Search, Edit2, Trash2, Tag, 
  Camera, Save, X, Filter, Percent, Check
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'fruits_legumes', label: 'Fruits & Légumes', icon: '🥦' },
  { value: 'viandes', label: 'Viandes', icon: '🥩' },
  { value: 'poissons', label: 'Poissons', icon: '🐟' },
  { value: 'produits_laitiers', label: 'Produits laitiers', icon: '🧀' },
  { value: 'epicerie', label: 'Épicerie', icon: '🍞' },
  { value: 'boissons', label: 'Boissons', icon: '🧋' },
  { value: 'surgeles', label: 'Surgelés', icon: '❄️' },
  { value: 'autre', label: 'Autre', icon: '📦' }
];

const UNITS = [
  { value: 'kg', label: 'Kg' },
  { value: 'piece', label: 'Pièce' },
  { value: 'litre', label: 'Litre' },
  { value: 'lot', label: 'Lot' },
  { value: '100g', label: '100g' }
];

export default function BusinessCatalogPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showPromoOnly, setShowPromoOnly] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef(null);

  const emptyProduct = {
    name: '',
    description: '',
    category: 'fruits_legumes',
    price: '',
    unit: 'kg',
    image: null,
    barcode: '',
    brand: '',
    is_local: false,
    producer_name: '',
    is_promotion: false,
    promotion_price: '',
    promotion_end_date: '',
    in_stock: true
  };

  const [formData, setFormData] = useState(emptyProduct);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/business/register');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/business/catalog`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur');
      const data = await res.json();
      setProducts(data);
    } catch (error) {
      toast.error('Erreur lors du chargement du catalogue');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      toast.error('Image trop grande (max 1 Mo)');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData({ ...formData, image: reader.result });
    };
    reader.readAsDataURL(file);
  };

  const openAddDialog = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setShowDialog(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category: product.category,
      price: product.price.toString(),
      unit: product.unit,
      image: product.image,
      barcode: product.barcode || '',
      brand: product.brand || '',
      is_local: product.is_local,
      producer_name: product.producer_name || '',
      is_promotion: product.is_promotion,
      promotion_price: product.promotion_price?.toString() || '',
      promotion_end_date: product.promotion_end_date || '',
      in_stock: product.in_stock
    });
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const token = localStorage.getItem('token');
    const payload = {
      ...formData,
      price: parseFloat(formData.price),
      promotion_price: formData.promotion_price ? parseFloat(formData.promotion_price) : null
    };

    try {
      const url = editingProduct 
        ? `${API_URL}/api/business/catalog/${editingProduct.id}`
        : `${API_URL}/api/business/catalog`;
      
      const res = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Erreur');
      }

      toast.success(editingProduct ? 'Produit mis à jour' : 'Produit ajouté');
      setShowDialog(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) return;

    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API_URL}/api/business/catalog/${productId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Erreur');
      
      toast.success('Produit supprimé');
      setProducts(products.filter(p => p.id !== productId));
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = !filterCategory || p.category === filterCategory;
    const matchesPromo = !showPromoOnly || p.is_promotion;
    return matchesSearch && matchesCategory && matchesPromo;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="font-semibold text-charcoal flex items-center gap-2">
              <Package className="w-5 h-5" />
              Mon catalogue
            </h1>
            <Button onClick={openAddDialog} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-4">
        {/* Filters */}
        <div className="bg-white rounded-xl p-4 mb-6 flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un produit..."
                className="pl-10"
              />
            </div>
          </div>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm"
          >
            <option value="">Toutes catégories</option>
            {CATEGORIES.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
            ))}
          </select>
          <button
            onClick={() => setShowPromoOnly(!showPromoOnly)}
            className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors ${
              showPromoOnly ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'
            }`}
          >
            <Percent className="w-4 h-4" />
            Promos uniquement
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-charcoal">{products.length}</p>
            <p className="text-sm text-muted-foreground">Produits</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{products.filter(p => p.in_stock).length}</p>
            <p className="text-sm text-muted-foreground">En stock</p>
          </div>
          <div className="bg-white rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{products.filter(p => p.is_promotion).length}</p>
            <p className="text-sm text-muted-foreground">En promo</p>
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="font-semibold text-charcoal mb-2">Aucun produit</h3>
            <p className="text-muted-foreground mb-4">
              {products.length === 0 
                ? 'Commencez par ajouter vos premiers produits au catalogue.'
                : 'Aucun produit ne correspond à votre recherche.'}
            </p>
            {products.length === 0 && (
              <Button onClick={openAddDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un produit
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-video bg-gray-100 relative">
                  {product.image ? (
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="text-4xl">
                        {CATEGORIES.find(c => c.value === product.category)?.icon || '📦'}
                      </span>
                    </div>
                  )}
                  {product.is_promotion && (
                    <span className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                      Promo
                    </span>
                  )}
                  {!product.in_stock && (
                    <span className="absolute top-2 right-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full">
                      Rupture
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h3 className="font-semibold text-charcoal">{product.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {CATEGORIES.find(c => c.value === product.category)?.label}
                        {product.brand && ` • ${product.brand}`}
                      </p>
                    </div>
                    <div className="text-right">
                      {product.is_promotion && product.promotion_price ? (
                        <>
                          <p className="text-lg font-bold text-red-600">{product.promotion_price.toFixed(2)}€</p>
                          <p className="text-xs text-muted-foreground line-through">{product.price.toFixed(2)}€</p>
                        </>
                      ) : (
                        <p className="text-lg font-bold text-charcoal">{product.price.toFixed(2)}€</p>
                      )}
                      <p className="text-xs text-muted-foreground">/ {product.unit}</p>
                    </div>
                  </div>
                  {product.is_local && (
                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full mb-2">
                      🌱 Producteur local
                    </span>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDialog(product)}>
                      <Edit2 className="w-3 h-3 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-500" onClick={() => handleDelete(product.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Product Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Image */}
            <div>
              <label className="text-sm font-medium mb-2 block">Photo du produit</label>
              <div 
                className="w-32 h-32 rounded-lg border-2 border-dashed border-gray-300 hover:border-primary cursor-pointer flex items-center justify-center overflow-hidden"
                onClick={() => imageInputRef.current?.click()}
              >
                {formData.image ? (
                  <img src={formData.image} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <Camera className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <input 
                ref={imageInputRef}
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleImageUpload}
              />
            </div>

            {/* Name & Category */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Nom du produit *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tomates cerises"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Catégorie *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  required
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.icon} {cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Price & Unit */}
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium mb-1 block">Prix *</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="4.99"
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Unité *</label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {UNITS.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1 block">Marque</label>
                <Input
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                  placeholder="Bio Village"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-sm font-medium mb-1 block">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description du produit..."
                className="w-full p-3 border rounded-lg resize-none h-20"
              />
            </div>

            {/* Barcode */}
            <div>
              <label className="text-sm font-medium mb-1 block">Code-barres (EAN)</label>
              <Input
                value={formData.barcode}
                onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                placeholder="3760000000000"
              />
            </div>

            {/* Local Producer */}
            <div className="p-4 bg-green-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_local}
                  onChange={(e) => setFormData({ ...formData, is_local: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium">Produit local / Producteur partenaire</span>
              </label>
              {formData.is_local && (
                <Input
                  className="mt-2"
                  value={formData.producer_name}
                  onChange={(e) => setFormData({ ...formData, producer_name: e.target.value })}
                  placeholder="Nom du producteur"
                />
              )}
            </div>

            {/* Promotion */}
            <div className="p-4 bg-red-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_promotion}
                  onChange={(e) => setFormData({ ...formData, is_promotion: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm font-medium flex items-center gap-1">
                  <Percent className="w-4 h-4" /> En promotion
                </span>
              </label>
              {formData.is_promotion && (
                <div className="grid md:grid-cols-2 gap-2 mt-2">
                  <div>
                    <label className="text-xs text-muted-foreground">Prix promo</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.promotion_price}
                      onChange={(e) => setFormData({ ...formData, promotion_price: e.target.value })}
                      placeholder="3.99"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Fin de promo</label>
                    <Input
                      type="date"
                      value={formData.promotion_end_date}
                      onChange={(e) => setFormData({ ...formData, promotion_end_date: e.target.value })}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Stock */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.in_stock}
                onChange={(e) => setFormData({ ...formData, in_stock: e.target.checked })}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium flex items-center gap-1">
                <Check className="w-4 h-4" /> En stock
              </span>
            </label>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Sauvegarde...' : (editingProduct ? 'Mettre à jour' : 'Ajouter')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}